import React from "react";
import { useLang } from "@/i18n/LangContext";

/* Sección Origen · Identidad cultural de Zenko · i18n */
const Origin = () => {
  const { t } = useLang();
  const o = t.origin;

  return (
    <section
      id="origen"
      className="relative py-24 lg:py-36 overflow-hidden"
      data-testid="origin-section"
    >
      {/* Watermark kanji */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 grid place-items-center select-none"
      >
        <div
          className="font-display font-extrabold text-[clamp(18rem,42vw,38rem)] leading-none tracking-tighter"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,229,160,0.08) 0%, rgba(0,229,160,0.015) 60%, transparent 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          善狐
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0D1B2A] via-transparent to-[#0D1B2A]" />

      <div className="relative max-w-5xl mx-auto px-5">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-px bg-[#00E5A0]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#00E5A0]">
            {o.eyebrow}
          </span>
        </div>

        {/* Title */}
        <h2 className="font-display font-extrabold text-[clamp(2.4rem,5.5vw,4.5rem)] leading-[0.95] tracking-tight mb-14">
          {o.title_q}<span className="italic font-light">{o.title_name}</span><span className="text-[#00E5A0]">{o.title_end}</span>
        </h2>

        {/* Character cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          {[
            { char: "善", label: o.char1_label, jp: o.char1_jp, kr: o.char1_kr, meaning: o.char1_meaning },
            { char: "狐", label: o.char2_label, jp: o.char2_jp, meaning: o.char2_meaning },
          ].map((c, i) => (
            <div
              key={i}
              data-testid={`origin-char-${i}`}
              className="zk-glass rounded-3xl p-6 lg:p-7 flex items-center gap-5 group hover:border-[#00E5A0]/40 transition-all"
            >
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-[#00E5A0]/20 blur-2xl rounded-full group-hover:bg-[#00E5A0]/30 transition" />
                <div
                  className="relative font-display font-extrabold text-[#00E5A0] leading-none"
                  style={{ fontSize: "clamp(4rem, 9vw, 6rem)" }}
                >
                  {c.char}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0] mb-1.5">
                  {c.label}
                </div>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="font-display font-bold text-white text-sm">{c.jp}</span>
                  {c.kr && (
                    <>
                      <span className="text-[#6B7A8D] text-xs">·</span>
                      <span className="font-display font-bold text-white text-sm">{c.kr}</span>
                    </>
                  )}
                </div>
                <div className="text-sm text-[#F5F7FA]/70 leading-snug">{c.meaning}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Story */}
        <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-10 lg:gap-12 items-start">
          <div className="space-y-6 text-[15px] lg:text-base text-[#F5F7FA]/75 leading-[1.75]">
            <p>
              {o.story_p1_a}
              <span className="text-white font-medium">{o.story_p1_b}</span>
              {o.story_p1_c}
              <span className="font-display font-bold text-[#00E5A0]">善狐</span>
              {o.story_p1_d}
              <em className="text-white">{o.story_p1_e}</em>
              {o.story_p1_f}
              <span className="text-white font-medium">{o.story_p1_g}</span>
              {o.story_p1_h}
            </p>

            <p>
              {o.story_p2_a}
              <span className="font-display font-bold text-[#00E5A0]">善</span>
              {o.story_p2_b}
              <span className="text-white font-medium">{o.story_p2_c}</span>
              {o.story_p2_d}
              <em className="text-white">{o.story_p2_e}</em>
              {o.story_p2_f}
            </p>
          </div>

          <div className="hidden lg:flex flex-col items-center justify-center self-stretch">
            <div className="w-px flex-1 bg-gradient-to-b from-transparent via-[#00E5A0]/40 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-[#00E5A0] my-2" />
            <div className="w-px flex-1 bg-gradient-to-b from-[#00E5A0]/40 via-transparent to-transparent" />
          </div>

          <div className="space-y-6 text-[15px] lg:text-base text-[#F5F7FA]/75 leading-[1.75]">
            <p>
              {o.story_right_a}
              <span className="text-white font-medium">{o.story_right_b}</span>
              {o.story_right_c}
            </p>
          </div>
        </div>

        {/* Manifesto */}
        <div className="mt-16 relative">
          <div className="relative zk-glass rounded-3xl p-8 lg:p-10 overflow-hidden border-l-[3px] border-[#00E5A0]">
            <div className="absolute -top-6 -right-4 font-display font-extrabold text-[10rem] leading-none text-[#00E5A0]/10 select-none pointer-events-none">
              &ldquo;
            </div>
            <div className="relative">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5A0] mb-4">
                {o.manifesto_eyebrow}
              </div>
              <p className="font-display font-bold text-[clamp(1.5rem,3.2vw,2.6rem)] leading-[1.15] tracking-tight">
                {o.manifesto_a}
                <span className="text-[#6B7A8D] font-normal italic">{o.manifesto_b}</span>
                <span className="text-[#00E5A0]">{o.manifesto_c}</span>
                <span className="text-[#00E5A0]">{o.manifesto_d}</span>
                {o.manifesto_e}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Origin;
